import { createRouter, createWebHistory } from 'vue-router'
import Homepage from '@/views/Homepage.vue'
import ComingSoon from '@/views/ComingSoon.vue'
import Login from '@/views/auth/Login.vue'
import { setupGuards } from '@/router/guards'
import { initAIReferralTracking } from '@/utils/analytics'
import * as Sentry from '@sentry/vue'

interface WindowWithGtag extends globalThis.Window {
  gtag?: (...args: unknown[]) => void
}

// Lazy load auth views
const ForgotPassword = () => import('@/views/auth/ForgotPassword.vue')
const ResetPassword = () => import('@/views/auth/ResetPassword.vue')

const CalculatorCategoryPage = () => import('@/views/CalculatorCategoryPage.vue')

// Lazy load calculator views for code splitting
const SteelColumnDesign = () => import('@/views/calculators/steel/SteelColumnDesign.vue')
const BasePlateDesign = () => import('@/views/calculators/steel/BasePlateDesign.vue')
const ConcBeamDesign = () => import('@/views/calculators/concrete/ConcBeamDesign.vue')
const CrackWidthCalculator = () => import('@/views/calculators/concrete/CrackWidthCalculator.vue')
const GantryGirderDesign = () => import('@/views/calculators/steel/GantryGirderDesign.vue')
const PurlinDesign = () => import('@/views/calculators/steel/PurlinDesign.vue')
const CalculatorsDirectory = () => import('@/views/CalculatorsDirectory.vue')
const ConcColumnDesign = () => import('@/views/calculators/concrete/ConcColumnDesign.vue')
const SlabDesign = () => import('@/views/calculators/concrete/SlabDesign.vue')
const RetainingWallDesign = () => import('@/views/calculators/concrete/RetainingWallDesign.vue')
const LoadCombinationGenerator = () => import('@/views/calculators/specialized/LoadCombinationGenerator.vue')
const StaadMto = () => import('@/views/calculators/specialized/StaadMto.vue')
const PebOptimizer = () => import('@/views/calculators/specialized/PebOptimizer.vue')
const Settings = () => import('@/views/Settings.vue')

const routes = [
  {
    path: '/tools/lookup',
    redirect: '/calculators/specialized/lookup'
  },
  {
    path: '/calculators/specialized/lookup',
    name: 'LookupTool',
    component: () => import('@/views/calculators/specialized/LookupTool.vue'),
    meta: {
      title: 'IS Code Lookup Tool',
      requiresAuth: true
    }
  },
  {
    path: '/downloads/peb-agent-setup',
    name: 'peb-agent-setup',
    component: () => import('@/views/PebAgentSetup.vue'),
    meta: {
      title: 'StructEngine PEB Agent Setup',
      description: 'STAAD.Pro User Tools setup guide for the StructEngine PEB desktop agent.'
    }
  },
  {
    path: '/calculators/specialized/peb-optimizer',
    name: 'peb-optimizer',
    component: () => import('@/views/calculators/specialized/PebOptimizer.vue'),
    meta: {
      title: 'PEB Optimizer | StructEngine',
      description: 'STAAD.Pro desktop-agent workflow for PEB rafter section optimization.',
      hideFooter: true,
      compactNavbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/',
    name: 'home',
    component: Homepage
  },
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: {
      title: 'Sign In - StructEngine',
      description: 'Sign in to your StructEngine account to access professional structural engineering calculations',
      requiresAuth: false,
      hideHeader: true,
      hideFooter: true
    }
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/Dashboard.vue'),
    meta: {
      title: 'Dashboard - StructEngine',
      description: 'Your StructEngine dashboard with calculation history and tools',
      requiresAuth: true
    }
  },
  {
    path: '/history',
    name: 'history',
    component: () => import('@/views/History.vue'),
    meta: {
      title: 'Calculation History - StructEngine',
      description: 'View and manage your past calculations',
      requiresAuth: true
    }
  },
  {
    path: '/settings',
    name: 'settings',
    component: Settings,
    meta: {
      title: 'Settings - StructEngine',
      description: 'Manage account and calculation preferences',
      requiresAuth: true,
    }
  },
  {
    path: '/projects/:id',
    name: 'project-detail',
    component: () => import('@/views/ProjectDetail.vue'),
    meta: {
      title: 'Project Details - StructEngine',
      description: 'View and manage project details and calculations',
      requiresAuth: true
    }
  },
  {
    path: '/steel-column',
    name: 'steel-column',
    component: SteelColumnDesign,
    meta: {
      title: 'Steel Column Design | IS 800:2007 Section 7',
      description: 'Steel compression member design calculator with buckling analysis per IS 800:2007',
      keywords: 'steel column design, compression member, buckling analysis, IS 800, structural steel',
      hideFooter: true,
      compactNavbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/base-plate',
    name: 'base-plate',
    component: BasePlateDesign,
    meta: {
      title: 'Base Plate Design | IS 800:2007',
      description: 'Steel column base plate design calculator per IS 800:2007',
      hideFooter: true,
      compactNavbar: true,
      requiresAuth: true
    }
  },
  // Legacy compatibility redirects
  {
    path: '/compression-member',
    redirect: '/steel-column'
  },
  // Archived analysis calculators: keep bookmarks out of unavailable tools.
  {
    path: '/calculators/beam-analysis',
    alias: ['/calculators/analysis/beam', '/beam-analysis', '/section-properties', '/calculators/analysis/section-properties'],
    redirect: '/calculators'
  },
  {
    path: '/button-demo',
    name: 'button-demo',
    component: ComingSoon,
    props: { title: 'Documentation', description: 'Documentation and tutorials are coming soon' },
    meta: {
      title: 'Documentation - StructEngine',
      description: 'Documentation and tutorials for the StructEngine platform',
      keywords: 'documentation, tutorials, help'
    }
  },
  {
    path: '/calculators/analysis',
    redirect: '/calculators'
  },
  {
    path: '/calculators/steel',
    name: 'calculators-steel',
    component: CalculatorCategoryPage,
    props: { domain: 'steel' },
    meta: { title: 'Steel Design Calculators (IS 800) - StructEngine', description: 'Steel column, base plate, gantry girder and purlin design calculators per IS 800:2007.' }
  },
  {
    path: '/calculators/concrete',
    name: 'calculators-concrete',
    component: CalculatorCategoryPage,
    props: { domain: 'concrete' },
    meta: { title: 'Concrete Design Calculators (IS 456) - StructEngine', description: 'Beam, slab, column, footing and retaining wall design per IS 456:2000.' }
  },
  {
    path: '/calculators/tools',
    redirect: '/calculators/specialized'
  },
  {
    path: '/calculators/specialized',
    name: 'calculators-specialized',
    component: CalculatorCategoryPage,
    props: { domain: 'specialized' },
    meta: { title: 'Structural Engineering Tools - StructEngine', description: 'IS code lookup, load combination generator and productivity tools.' }
  },
  {
    path: '/calculators',
    name: 'calculators',
    component: CalculatorsDirectory,
    meta: {
      title: 'All Calculators - StructEngine',
      description: 'Explore StructEngine structural engineering calculators across steel, concrete, and specialized workflows. '
        + 'Access live tools, beta interfaces, backend-ready modules, and planned releases in one catalog.'
    }
  },
  {
    path: '/beams',
    name: 'beams',
    component: ComingSoon,
    props: { title: 'Beam Design', description: 'Beam design' }
  },
  {
    path: '/register',
    redirect: '/login'  // Registration happens via Google OAuth on login page
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: ForgotPassword,
    meta: {
      title: 'Reset Password - StructEngine',
      description: 'Reset your StructEngine account password',
      requiresAuth: false
    }
  },
  {
    path: '/auth/verify',
    name: 'EmailVerification',
    component: () => import('@/views/auth/EmailVerification.vue'),
    meta: {
      title: 'Verify Email - StructEngine',
      requiresAuth: false
    }
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: ResetPassword,
    meta: {
      title: 'Set New Password - StructEngine',
      description: 'Set a new password for your StructEngine account',
      requiresAuth: false
    }
  },
  {
    path: '/calculators/concrete/conc-beam-design',
    name: 'conc-beam-design',
    component: ConcBeamDesign,
    meta: {
      title: 'IS 456 Reinforced Concrete Beam Design Calculator',
      description: 'Professional beam design calculator for reinforced concrete beams per IS 456:2000. Calculate flexural and shear design with serviceability checks.',
      keywords: 'concrete beam design, reinforced concrete, IS 456, flexural design, shear design, structural engineering',
      hideFooter: true,
      compactNavbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/calculators/concrete/crack-width',
    name: 'crack-width',
    component: CrackWidthCalculator,
    meta: {
      title: 'IS 456 Crack Width Calculator - Flexural Members',
      description: 'Professional crack width calculator for concrete flexural members per IS 456:2000 Annex F. Calculate crack width in beams and slabs under service loads.',
      keywords: 'crack width calculator, concrete crack width, IS 456, flexural members, serviceability, structural engineering',
      hideFooter: true,
      compactNavbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/calculators/concrete/column-design',
    name: 'column-design',
    component: ConcColumnDesign,
    meta: {
      title: 'IS 456 Reinforced Concrete Column Design Calculator',
      description: 'Professional column design calculator for reinforced concrete columns per IS 456:2000. Calculate axial and biaxial bending design with slenderness effects.',
      keywords: 'column design, reinforced concrete, IS 456, axial load, biaxial bending, slenderness, structural engineering',
      hideFooter: true,
      compactNavbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/calculators/concrete/slab-design',
    name: 'slab-design',
    component: SlabDesign,
    meta: {
      title: 'IS 456 Slab Design Calculator - One-way and Two-way Slabs',
      description: 'Professional slab design calculator for reinforced concrete slabs per IS 456:2000. Supports one-way and two-way slabs with comprehensive support conditions and moment coefficients.',
      keywords: 'slab design, concrete slab, one-way slab, two-way slab, IS 456, reinforced concrete, structural engineering',
      hideFooter: true,
      compactNavbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/calculators/concrete/retaining-wall',
    name: 'retaining-wall',
    component: RetainingWallDesign,
    meta: {
      title: 'IS 456 Retaining Wall Design Calculator - Cantilever and Counterfort',
      description: 'Professional retaining wall design calculator for cantilever and counterfort walls per IS 456:2000. Includes stability analysis (overturning, sliding, bearing pressure) and reinforcement design.',
      keywords: 'retaining wall design, cantilever wall, counterfort wall, IS 456, stability analysis, reinforced concrete, structural engineering',
      hideFooter: true,
      compactNavbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/calculators/concrete/footing-design',
    name: 'footing-design',
    component: () => import('@/views/calculators/concrete/FootingDesign.vue'),
    meta: {
      title: 'IS 456 Isolated Footing Design Calculator',
      description: 'Professional isolated footing design calculator per IS 456:2000. Calculate bearing pressure, stability checks (sliding, overturning, buoyancy), and reinforcement design for column footings.',
      keywords: 'footing design, isolated footing, IS 456, bearing pressure, stability analysis, reinforcement design, structural engineering',
      hideFooter: true,
      compactNavbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/calculators/steel/gantry-girder',
    name: 'gantry-girder',
    component: GantryGirderDesign,
    meta: {
      title: 'IS 800 Gantry Girder Design Calculator',
      description: 'Professional gantry girder design calculator for steel structures per IS 800:2007. Includes crane loads, fatigue analysis, deflection checks, and code compliance verification.',
      keywords: 'gantry girder design, steel structure, IS 800, crane loads, fatigue analysis, deflection, structural engineering',
      hideFooter: true,
      compactNavbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/calculators/steel/purlin-design',
    name: 'purlin-design',
    component: PurlinDesign,
    meta: {
      title: 'IS 801 Purlin Design Calculator - Cold-Formed Steel',
      description: 'Professional purlin design calculator for cold-formed steel structures per IS 801:1975. Includes stiffener analysis, web crippling, biaxial bending, and deflection checks.',
      keywords: 'purlin design, cold-formed steel, IS 801, stiffener analysis, web crippling, biaxial bending, structural engineering',
      hideFooter: true,
      compactNavbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/calculators/load-combinations',
    redirect: '/calculators/specialized/load-combinations'
  },
  {
    path: '/calculators/specialized/load-combinations',
    name: 'LoadCombinations',
    component: LoadCombinationGenerator,
    meta: {
      title: 'Load Combination Generator | StructEngine',
      description: 'Generate STAAD.Pro load combinations per IS 800:2007',
      keywords: 'load combinations, staad pro, is 800, structural analysis',
      hideFooter: true,
      compactNavbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/calculators/specialized/staad-plate-section-mto',
    name: 'StaadMto',
    component: StaadMto,
    meta: {
      title: 'STAAD Procurement-Ready MTO | StructEngine',
      description: 'Generate full structural steel BOQ with plate-wise breakdown from STAAD .std files.',
      keywords: 'staad mto, structural steel boq, plate takeoff, steel procurement',
      hideFooter: true,
      compactNavbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/calculators/specialized/peb-optimizer',
    name: 'PebOptimizer',
    component: PebOptimizer,
    meta: {
      title: 'PEB Optimizer | STAAD.Pro Automation',
      description: 'Optimize pre-engineered building rafter sections through iterative STAAD.Pro analysis.',
      keywords: 'peb optimizer, staad pro automation, rafter optimization, IS 800, structural engineering',
      hideFooter: true,
      compactNavbar: true,
      requiresAuth: true
    }
  },
  // New routes to match navbar links
  {
    path: '/calculators/concrete/beam-design',
    redirect: '/calculators/concrete/conc-beam-design'
  },
  {
    path: '/calculators/steel/steel-column',
    redirect: '/steel-column'
  },
  {
    path: '/calculators/steel/base-plate',
    redirect: '/base-plate'
  },
  {
    path: '/beam-column-connection',
    name: 'beam-column-connection',
    component: ComingSoon,
    props: { title: 'Beam-Column Connection Design', description: 'Beam-column connection design' }
  },
  {
    path: '/docs',
    redirect: '/documentation'
  },
  {
    path: '/blog',
    name: 'blog',
    component: () => import('@/views/Blog.vue'),
    meta: {
      title: 'Engineering Blog - StructEngine',
      description: 'Technical articles, design guides, and insights on structural engineering and IS codes'
    }
  },
  {
    path: '/blog/:slug',
    name: 'blog-post',
    component: () => import('@/views/BlogPost.vue'),
    meta: {
      title: 'Blog Post - StructEngine',
      description: 'Read our latest technical articles and structural engineering insights'
    }
  },
  {
    path: '/about',
    name: 'about',
    component: () => import('@/views/About.vue'),
    meta: {
      title: 'About Us - StructEngine',
      description: 'Learn about StructEngine and our mission to transform structural engineering'
    }
  },
  {
    path: '/pricing',
    redirect: '/calculators'
  },
  {
    path: '/contact',
    name: 'contact',
    component: () => import('@/views/Contact.vue'),
    meta: {
      title: 'Contact Us - StructEngine',
      description: 'Get in touch with the StructEngine team'
    }
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('@/views/Privacy.vue'),
    meta: {
      title: 'Privacy Policy - StructEngine',
      description: 'How we handle your data and protect your privacy'
    }
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('@/views/Terms.vue'),
    meta: {
      title: 'Terms of Service - StructEngine',
      description: 'Terms and conditions for using StructEngine'
    }
  },
  {
    path: '/cookie-settings',
    name: 'cookie-settings',
    component: () => import('@/views/CookieSettings.vue'),
    meta: {
      title: 'Cookie Settings - StructEngine',
      description: 'Manage your cookie preferences'
    }
  },
  {
    path: '/disclaimer',
    name: 'disclaimer',
    component: () => import('@/views/Disclaimer.vue'),
    meta: {
      title: 'Engineering Disclaimer - StructEngine',
      description: 'Professional engineering disclaimer and liability information'
    }
  },
  {
    path: '/:catchAll(.*)',
    name: 'not-found',
    component: ComingSoon,
    props: { title: 'Coming Soon', description: 'This page is under development' }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // If the user navigated using browser back/forward buttons, restore saved position
    if (savedPosition) {
      return savedPosition
    }
    // If navigating to a hash anchor, scroll to that element
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth'
      }
    }
    // Otherwise, scroll to top of page
    return { top: 0, behavior: 'smooth' }
  }
})

// Prefetch likely next routes based on current route
router.afterEach((to) => {
  if ('requestIdleCallback' in window) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ; (window as any).requestIdleCallback(() => {
      const prefetchMap: Record<string, string[]> = {
        '/': ['/login', '/calculators'],
        '/login': ['/calculators'],
        '/calculators': [
          '/calculators/concrete/conc-beam-design',
          '/steel-column'
        ]
      }

      const nextRoutes = prefetchMap[to.path] || []
      nextRoutes.forEach((path) => {
        const route = router.resolve(path)
        route.matched.forEach((record) => {
          const component = record.components?.default
          if (typeof component === 'function') {
            // Trigger the dynamic import to start downloading the chunk
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ; (component as any)()
          }
        })
      })
    })
  }
})

// GEO analytics: track AI referral landing sessions on calculator routes
initAIReferralTracking(router)

/* SECURITY: setupGuards(router) was never called before this commit.
   All requiresAuth: true routes were unenforced. Now fixed. */
setupGuards(router)

// Task 5 — Dynamic import error handler
// Catches chunk-load failures (network error, stale hash after deploy).
// Hard-reloads once to pick up the latest bundle; prevents silent stranding.
router.onError((error, to) => {
  const isDynamicImportError =
    error instanceof TypeError &&
    (error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed') ||
      error.message.includes('error loading dynamically imported module'))

  if (isDynamicImportError) {
    const reloadedKey = `_router_reload_${to.fullPath}`
    if (!sessionStorage.getItem(reloadedKey)) {
      sessionStorage.setItem(reloadedKey, '1')
      window.location.assign(to.fullPath)
    } else {
      // Already retried — capture to Sentry so we know the chunk is missing
      Sentry.captureException(error, {
        tags: { type: 'dynamic_import_failure' },
        extra: { route: to.fullPath },
      })
    }
  }
})

// Task 9 — GA4 page_view events for SPA navigation
// GA4 auto-collects the first page view on load; subsequent navigations need
// a manual event because the page never fully reloads.
router.afterEach((to) => {
  const gtag = (window as WindowWithGtag).gtag
  if (typeof gtag !== 'function') return
  gtag('event', 'page_view', {
    page_path: to.fullPath,
    page_title: typeof to.meta.title === 'string' ? to.meta.title : document.title,
  })
})

export default router
